import React, { useState, useEffect, useCallback, useMemo } from "react";
import type { Machine } from "../services/BackendDataService";
import {
  peekMotorMonitoringService,
  type MotorState,
  type StreamInfoCallback,
} from "../services/MotorMonitoringService";
import { useTheme } from "../providers/ThemeProvider";
import { useMotorStates } from "../hooks/useMotorStates";
import SeriesChart, {
  type SeriesDef,
  type AxisDef,
} from "../dashboard/panels/SeriesChart";
import WaitingState from "../dashboard/panels/WaitingState";
import ConnectionStatus from "../dashboard/panels/ConnectionStatus";
import { DashboardSyncProvider } from "../dashboard/DashboardSyncContext";
import { formatMetricValue, isLiveWithin } from "../dashboard/format";
import type { SeriesPoint } from "../dashboard";

const cx = (...classes: (string | undefined | null | false)[]) =>
  classes.filter(Boolean).join(" ");

// ─── Shared motor chart definition (same SeriesChart the energy screen uses) ──
// Keys are MotorHistoryPoint fields. Three y-axes: temperatures, torque, and a
// combined hours/mm/rpm axis — kept at module scope so the object identity is
// stable across renders (an inline array would rebuild the chart every flush).
const MOTOR_Y_AXES: AxisDef[] = [
  { title: "°C", color: "#ef4444", opposite: false },
  { title: "Nm", color: "#3b82f6", opposite: false },
  { title: "hours / mm / rpm", color: "#f59e0b", opposite: true },
];

const MOTOR_SERIES_DEFS: SeriesDef[] = [
  {
    key: "tempMotor",
    name: "Temperature motor",
    color: "#ef4444",
    unit: "°C",
    axis: 0,
  },
  {
    key: "torqueRms",
    name: "Torque RMS",
    color: "#3b82f6",
    unit: "Nm",
    axis: 1,
  },
  {
    key: "tempAmplifier",
    name: "Temperature amplifier",
    color: "#f97316",
    unit: "°C",
    axis: 0,
  },
  {
    key: "ambientTemperature",
    name: "Ambient temperature",
    color: "#f59e0b",
    unit: "°C",
    axis: 0,
  },
  { key: "velocity", name: "Velocity", color: "#eab308", unit: "rpm", axis: 2 },
  {
    key: "fbActHours",
    name: "Operating hours",
    color: "#1f2937",
    unit: "h",
    axis: 2,
  },
  {
    key: "lagDistanceRms",
    name: "Lag distance RMS",
    color: "#8b5cf6",
    unit: "°/mm",
    axis: 2,
  },
  {
    key: "lagDistanceDc",
    name: "Lag distance DC",
    color: "#6366f1",
    unit: "°/mm",
    axis: 2,
  },
];

interface MotorConditionMonitoringProps {
  machineData?: Machine | null;
  /** ISO-8601 start time for JetStream replay; undefined = live only */
  startTime?: string;
  /** When true, incoming data updates are frozen so user can zoom freely */
  paused?: boolean;
  /** Called once with the stream's oldest available timestamp */
  onStreamInfo?: StreamInfoCallback;
  /**
   * Minimum ms between overview history points during replay.
   * Wider ranges pass a larger interval to keep point count manageable.
   */
  sampleIntervalMs?: number;
}

// ─── Motor list card ────────────────────────────────────────────────────────
interface MotorCardProps {
  motor: MotorState;
  onClick: (motor: MotorState) => void;
}

const MotorCard: React.FC<MotorCardProps> = ({ motor, onClick }) => {
  const { currentTheme } = useTheme();
  const isDark = currentTheme.isDark;
  const s = motor.signals;

  // Tick every 3 s so the live/idle badge re-evaluates without relying on a
  // stale Date.now() captured at render time.
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 3000);
    return () => clearInterval(id);
  }, []);

  const isLive = isLiveWithin(motor.lastUpdate);

  return (
    <button
      onClick={() => onClick(motor)}
      className={cx(
        "w-full text-left rounded-xl border p-4 transition-all duration-200 hover:shadow-md hover:scale-[1.01]",
        isDark
          ? "bg-gray-800 border-gray-700 hover:border-blue-500"
          : "bg-white border-gray-200 hover:border-blue-400",
      )}
    >
      {/* Header row */}
      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <span
            className={cx(
              "inline-flex items-center justify-center w-8 h-8 rounded-lg text-xs font-bold flex-shrink-0",
              isDark
                ? "bg-blue-900 text-blue-300"
                : "bg-blue-100 text-blue-700",
            )}
          >
            M
          </span>
          <div className="min-w-0 flex-1">
            <div
              className={cx(
                "font-semibold text-sm truncate",
                isDark ? "text-white" : "text-gray-900",
              )}
              title={motor.shortName}
            >
              {motor.shortName}
            </div>
            <div
              className={cx(
                "text-xs truncate",
                isDark ? "text-gray-500" : "text-gray-400",
              )}
              title={motor.id}
            >
              {motor.id}
            </div>
          </div>
        </div>
        <span
          className={cx(
            "inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full flex-shrink-0",
            isLive
              ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
              : "bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400",
          )}
        >
          <span
            className={cx(
              "w-1.5 h-1.5 rounded-full",
              isLive ? "bg-green-500 animate-pulse" : "bg-gray-400",
            )}
          />
          {isLive ? "Live" : "Idle"}
        </span>
      </div>

      {/* KPI grid */}
      <div className="grid grid-cols-2 gap-2">
        {[
          {
            label: "Torque RMS",
            value: formatMetricValue(s.torqueRms, { decimals: 2, unit: "Nm" }),
          },
          {
            label: "Motor temp",
            value: formatMetricValue(s.tempMotor, { decimals: 1, unit: "°C" }),
          },
          {
            label: "Lag dist. RMS",
            value: formatMetricValue(s.lagDistanceRms, {
              decimals: 3,
              unit: "°/mm",
            }),
          },
          {
            label: "Op. hours",
            value:
              typeof s.fbActHours === "number"
                ? `${Math.round(s.fbActHours)} h`
                : formatMetricValue(s.fbHours, { decimals: 0, unit: "h" }),
          },
        ].map(({ label, value }) => (
          <div
            key={label}
            className={cx(
              "rounded-lg p-2",
              isDark ? "bg-gray-700/60" : "bg-gray-50",
            )}
          >
            <div
              className={cx(
                "text-xs mb-0.5",
                isDark ? "text-gray-400" : "text-gray-500",
              )}
            >
              {label}
            </div>
            <div
              className={cx(
                "text-sm font-semibold",
                isDark ? "text-gray-100" : "text-gray-800",
              )}
            >
              {value}
            </div>
          </div>
        ))}
      </div>

      {s.motorTyp && (
        <div
          className={cx(
            "mt-2 text-xs truncate",
            isDark ? "text-gray-500" : "text-gray-400",
          )}
        >
          Type: {s.motorTyp}
        </div>
      )}
    </button>
  );
};

// ─── KPI chip ───────────────────────────────────────────────────────────────
const KpiChip: React.FC<{
  label: string;
  value: string;
  highlight?: boolean;
}> = ({ label, value, highlight }) => {
  const { currentTheme } = useTheme();
  const isDark = currentTheme.isDark;
  return (
    <div
      className={cx(
        "flex flex-col items-center justify-center rounded-xl border px-4 py-3 min-w-[110px] flex-1",
        isDark ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200",
      )}
    >
      <span
        className={cx(
          "text-xs mb-1",
          isDark ? "text-gray-400" : "text-gray-500",
        )}
      >
        {label}
      </span>
      <span
        className={cx(
          "text-base font-bold",
          highlight ? "text-blue-500" : isDark ? "text-white" : "text-gray-900",
        )}
      >
        {value}
      </span>
    </div>
  );
};

// ─── Motor detail dashboard ──────────────────────────────────────────────────
const MotorDashboard: React.FC<{
  motor: MotorState;
  onBack: () => void;
  replaying?: boolean;
  /** Called when user clicks the Full Resolution button for the visible range */
  onDetailRequest?: (startIso: string, endIso: string) => void;
  machineBK?: string;
}> = ({ motor, onBack, replaying, onDetailRequest, machineBK }) => {
  const { currentTheme } = useTheme();
  const isDark = currentTheme.isDark;
  const s = motor.signals;

  const fmtNum = (v?: number | string, dec = 1, unit = "") =>
    formatMetricValue(v, { decimals: dec, unit: unit.trim() || undefined });

  const opHours =
    s.fbActHours !== undefined
      ? Math.round(s.fbActHours)
      : s.fbHours !== undefined
        ? s.fbHours
        : undefined;

  // motor.history is mutated in place (push), so build the channel Map the
  // shared SeriesChart expects, re-derived only when the point count changes.
  const historyLen = motor.history.length;
  const chartData = useMemo(() => {
    const map = new Map<string, SeriesPoint[]>();
    for (const def of MOTOR_SERIES_DEFS) map.set(def.key, []);
    for (const point of motor.history) {
      for (const def of MOTOR_SERIES_DEFS) {
        const v = (point as unknown as Record<string, unknown>)[def.key];
        if (typeof v === "number") {
          map.get(def.key)!.push({ t: point.timestamp, v });
        }
      }
    }
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [motor.id, historyLen]);

  const handleDetail = useCallback(
    (startMs: number, endMs: number) => {
      onDetailRequest?.(
        new Date(startMs).toISOString(),
        new Date(endMs).toISOString(),
      );
    },
    [onDetailRequest],
  );

  return (
    <div className="space-y-4">
      {/* Back + title */}
      <div className="flex items-center gap-3">
        <button
          onClick={onBack}
          className={cx(
            "p-2 rounded-lg border transition-colors",
            isDark
              ? "border-gray-600 hover:bg-gray-700 text-gray-300"
              : "border-gray-300 hover:bg-gray-100 text-gray-600",
          )}
        >
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
        </button>
        <div>
          <h2
            className={cx(
              "font-semibold text-lg",
              isDark ? "text-white" : "text-gray-900",
            )}
          >
            {motor.shortName}
          </h2>
          <div
            className={cx(
              "text-xs",
              isDark ? "text-gray-500" : "text-gray-400",
            )}
          >
            {motor.id}
          </div>
        </div>
      </div>

      {/* Info cards row */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {[
          {
            label: "Operating hours",
            value:
              opHours !== undefined ? `${opHours.toLocaleString()} h` : "—",
            highlight: true,
          },
          { label: "Part No.", value: s.motorPartNo || "—", highlight: false },
          { label: "Motor Type", value: s.motorTyp || "—", highlight: false },
        ].map(({ label, value, highlight }) => (
          <div
            key={label}
            className={cx(
              "rounded-xl border p-4",
              isDark
                ? "bg-gray-800 border-gray-700"
                : "bg-white border-gray-200",
            )}
          >
            <div
              className={cx(
                "text-xs mb-1",
                isDark ? "text-gray-400" : "text-gray-500",
              )}
            >
              {label}
            </div>
            <div
              className={cx(
                "text-xl font-bold",
                highlight
                  ? "text-blue-500"
                  : isDark
                    ? "text-white"
                    : "text-gray-900",
              )}
            >
              {value}
            </div>
          </div>
        ))}
      </div>

      {/* KPI chips */}
      <div className="flex flex-wrap gap-2">
        <KpiChip
          label="Ambient temp"
          value={fmtNum(s.ambientTemperature, 1, " °C")}
        />
        <KpiChip label="Motor temp" value={fmtNum(s.tempMotor, 1, " °C")} />
        <KpiChip
          label="Amplifier temp"
          value={fmtNum(s.tempAmplifier, 1, " °C")}
        />
        <KpiChip
          label="Torque RMS"
          value={fmtNum(s.torqueRms, 2, " Nm")}
          highlight
        />
        <KpiChip label="Torque DC" value={fmtNum(s.torqueDc, 3, " Nm")} />
        <KpiChip
          label="Lagdist. RMS"
          value={fmtNum(s.lagDistanceRms, 3, " °/mm")}
        />
        <KpiChip
          label="Lagdist. DC"
          value={fmtNum(s.lagDistanceDc, 7, " °/mm")}
        />
      </div>

      {/* Chart — the shared SeriesChart, same as the energy screen */}
      <div
        className={cx(
          "rounded-xl border p-4",
          isDark ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200",
        )}
      >
        <div
          className={cx(
            "text-xs font-medium mb-3",
            isDark ? "text-gray-400" : "text-gray-500",
          )}
        >
          Measurements
        </div>
        <div className="relative h-80">
          <DashboardSyncProvider>
            <SeriesChart
              seriesDefs={MOTOR_SERIES_DEFS}
              yAxes={MOTOR_Y_AXES}
              data={chartData}
              height={320}
              canDetail={!!onDetailRequest}
              onDetail={handleDetail}
              commentType="motor_monitoring"
              machineBK={machineBK}
            />
          </DashboardSyncProvider>
          {/* Overlay spinner while history is still loading */}
          {motor.history.length === 0 && (
            <div className="absolute inset-0 flex items-center justify-center bg-white/60 dark:bg-gray-800/60 rounded-lg">
              <div className="text-center">
                {replaying ? (
                  <>
                    <div className="w-6 h-6 border-2 border-blue-100 border-t-blue-500 rounded-full animate-spin mx-auto mb-2" />
                    <p
                      className={cx(
                        "text-xs",
                        isDark ? "text-gray-500" : "text-gray-400",
                      )}
                    >
                      Loading history…
                    </p>
                  </>
                ) : (
                  <>
                    <svg
                      className={cx(
                        "w-8 h-8 mx-auto mb-2",
                        isDark ? "text-gray-600" : "text-gray-300",
                      )}
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.5}
                        d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                      />
                    </svg>
                    <p
                      className={cx(
                        "text-xs",
                        isDark ? "text-gray-500" : "text-gray-400",
                      )}
                    >
                      No data for this time range
                    </p>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Main component ──────────────────────────────────────────────────────────
const MotorConditionMonitoring: React.FC<MotorConditionMonitoringProps> = ({
  machineData,
  startTime,
  paused = false,
  onStreamInfo,
  sampleIntervalMs,
}) => {
  const { currentTheme } = useTheme();
  const isDark = currentTheme.isDark;

  const [selectedMotorId, setSelectedMotorId] = useState<string | null>(null);

  // Shared axis-stream subscription (same pipeline the Servo screen uses):
  // live → NATS worker, date range → Doris (works even with no live data).
  const { motors, isConnected, subscribed, replaying, machineBK } =
    useMotorStates(machineData, startTime, paused, {
      onStreamInfo,
      sampleIntervalMs,
    });

  // Request full-resolution data for the visible zoom range (user-triggered)
  const handleDetailRequest = useCallback(
    (startIso: string, endIso: string) => {
      if (!machineBK) return;
      peekMotorMonitoringService(machineBK)?.loadRangeDetail(startIso, endIso);
    },
    [machineBK],
  );

  const selectedMotor = selectedMotorId ? motors.get(selectedMotorId) : null;

  // Notify the service when the selected motor changes so it only loads
  // history for the chosen motor (instead of all motors on the machine).
  const handleSelectMotor = useCallback(
    (motorId: string | null) => {
      setSelectedMotorId(motorId);
      if (machineBK) {
        peekMotorMonitoringService(machineBK)?.setSelectedMotor(motorId);
      }
    },
    [machineBK],
  );

  // ── Not connected (live only — a date range loads from Doris regardless) ──
  if (!isConnected && !startTime) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[300px] gap-3">
        <svg
          className={cx(
            "w-12 h-12",
            isDark ? "text-gray-600" : "text-gray-300",
          )}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1}
            d="M18.364 5.636a9 9 0 010 12.728M15.536 8.464a5 5 0 010 7.072M12 12h.01M6.343 6.343a9 9 0 000 12.728"
          />
        </svg>
        <p
          className={cx("text-sm", isDark ? "text-gray-400" : "text-gray-500")}
        >
          NATS not connected
        </p>
      </div>
    );
  }

  // ── No machine data ────────
  if (!machineBK) {
    return (
      <div className="flex items-center justify-center min-h-[300px]">
        <p
          className={cx("text-sm", isDark ? "text-gray-400" : "text-gray-500")}
        >
          No machine selected
        </p>
      </div>
    );
  }

  // ── Motor detail ───────────
  if (selectedMotor) {
    return (
      <MotorDashboard
        motor={selectedMotor}
        onBack={() => handleSelectMotor(null)}
        replaying={replaying}
        onDetailRequest={handleDetailRequest}
        machineBK={machineBK}
      />
    );
  }

  // ── Motor list ─────────────
  const motorList = Array.from(motors.values()).sort((a, b) =>
    a.id.localeCompare(b.id),
  );

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <ConnectionStatus
          connected={motorList.length > 0}
          label={
            motorList.length > 0
              ? `${motorList.length} motor${motorList.length > 1 ? "s" : ""} detected`
              : subscribed
                ? "Waiting for motor data…"
                : "Connecting…"
          }
        />
        {replaying && (
          <span className="text-xs text-blue-500 animate-pulse">
            Replaying…
          </span>
        )}
      </div>

      {/* Motor cards grid */}
      {motorList.length === 0 ? (
        <WaitingState
          title={
            startTime ? "Loading history…" : "Listening for motor signals…"
          }
          subject={startTime ? undefined : `edge-data.live.*.${machineBK}.>`}
          detail={
            startTime
              ? "No motors have reported yet for this range."
              : undefined
          }
        />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {motorList.map((motor) => (
            <MotorCard
              key={motor.id}
              motor={motor}
              onClick={(m) => handleSelectMotor(m.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default MotorConditionMonitoring;
